/**
 * Allegati di un'offerta, di un'offerta consulente o di una commessa.
 *
 * In creazione l'elemento non esiste ancora: i file scelti restano in attesa
 * e vengono caricati dopo il salvataggio (la pagina chiama caricaInSospeso).
 */
import { useEffect, useState } from 'react';
import { allegatiApi } from '../api';

const CATEGORIE = [
  { v: 'ordine', l: 'Ordine di acquisto' },
  { v: 'offerta_firmata', l: 'Offerta firmata' },
  { v: 'contratto', l: 'Contratto' },
  { v: 'preventivo', l: 'Preventivo ricevuto' },
  { v: 'altro', l: 'Altro' },
];
const etichetta = (v) => CATEGORIE.find(c => c.v === v)?.l || v;
const kb = (n) => n < 1024 * 1024 ? `${Math.round(n / 1024)} KB` : `${(n / 1048576).toFixed(1)} MB`;

const STILE = {
  ordine: 'bg-brand-50 text-brand-700',
  offerta_firmata: 'bg-green-100 text-green-700',
  contratto: 'bg-purple-100 text-purple-700',
  preventivo: 'bg-blue-100 text-blue-700',
  altro: 'bg-gray-100 text-gray-600',
};

// Documenti che provano l'accettazione del cliente: caricandone uno,
// l'offerta si può considerare vinta.
export const CATEGORIE_ACCETTAZIONE = ['ordine', 'offerta_firmata', 'contratto'];

export default function PannelloAllegati({
  tipo, id, inSospeso, onInSospeso, onCaricato,
  categorie = ['ordine', 'offerta_firmata', 'contratto', 'altro'],
  titolo = 'Allegati',
  descrizione = 'Ordini di acquisto, offerte firmate per accettazione, contratti.',
}) {
  const [righe, setRighe] = useState([]);
  const [categoria, setCategoria] = useState(categorie[0]);
  const [caricando, setCaricando] = useState(false);

  const carica = () => {
    if (!id) { setRighe([]); return; }
    allegatiApi.list(tipo, id).then(setRighe).catch(() => setRighe([]));
  };
  useEffect(() => { carica(); }, [tipo, id]);

  const scegli = async (file) => {
    if (!file) return;
    if (!id) {
      // Elemento non ancora salvato: si tiene da parte con la sua categoria
      onInSospeso?.([...(inSospeso || []), { file, categoria }]);
      return;
    }
    setCaricando(true);
    try {
      const a = await allegatiApi.carica(tipo, id, file, categoria);
      carica();
      onCaricato?.(a);   // chi ci contiene decide se proporre l'accettazione
    }
    catch (e) { alert(e); } finally { setCaricando(false); }
  };

  const elimina = async (a) => {
    if (!confirm(`Eliminare l'allegato "${a.nome_file}"?`)) return;
    try { await allegatiApi.elimina(a.id); carica(); } catch (e) { alert(e); }
  };

  const opzioni = CATEGORIE.filter(c => categorie.includes(c.v));

  return (
    <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
      <div>
        <div className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{titolo}</div>
        <p className="text-xs text-gray-500 mt-0.5">{descrizione}</p>
      </div>

      {righe.map(a => (
        <div key={a.id} className="flex items-center gap-2 bg-white border rounded px-2 py-1.5 text-sm">
          <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${STILE[a.categoria] || STILE.altro}`}>
            {etichetta(a.categoria)}
          </span>
          <button className="text-brand-600 hover:underline flex-1 truncate"
                  onClick={() => allegatiApi.scarica(a.id, a.nome_file).catch(e => alert(e))}>📎 {a.nome_file}</button>
          <span className="text-xs text-gray-400 shrink-0">{kb(a.dimensione || 0)}</span>
          <button type="button" onClick={() => elimina(a)}
                  className="text-gray-400 hover:text-red-600 px-1" title="Elimina allegato">✕</button>
        </div>
      ))}

      {/* File scelti prima del salvataggio */}
      {(inSospeso || []).map((x, i) => (
        <div key={`s${i}`} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-sm">
          <span className="text-xs px-2 py-0.5 rounded-full bg-white text-gray-600 shrink-0">{etichetta(x.categoria)}</span>
          <span className="flex-1 truncate text-gray-700">{x.file.name}</span>
          <span className="text-xs text-amber-700 shrink-0">al salvataggio</span>
          <button type="button" className="text-gray-400 hover:text-red-600 px-1"
                  onClick={() => onInSospeso(inSospeso.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}

      {righe.length === 0 && !(inSospeso || []).length && (
        <p className="text-xs text-gray-400 text-center py-2">Nessun allegato</p>
      )}

      <div className="flex items-center gap-2">
        <select className="input py-1 text-xs max-w-[190px]" value={categoria}
                onChange={e => setCategoria(e.target.value)}>
          {opzioni.map(c => <option key={c.v} value={c.v}>{c.l}</option>)}
        </select>
        <input type="file" className="text-xs flex-1" accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
               onChange={e => { scegli(e.target.files?.[0]); e.target.value = ''; }} />
        {caricando && <span className="text-xs text-gray-500 shrink-0">carico…</span>}
      </div>
    </div>
  );
}

/** Carica i file rimasti in attesa dopo che l'elemento è stato salvato. */
export async function caricaInSospeso(tipo, id, inSospeso) {
  for (const x of inSospeso || []) {
    await allegatiApi.carica(tipo, id, x.file, x.categoria);
  }
}
